import React, { useState } from 'react'
import {
  Stack,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'

export type ActionColor =
  'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'

export interface RowActionDef {
  label: string
  icon: React.ReactElement
  onClick: () => void
  color?: ActionColor
  disabled?: boolean
  show?: boolean
}

interface RowActionMenuProps {
  inlineActions?: RowActionDef[]
  menuActions?: RowActionDef[]
}

const RowActionMenu: React.FC<RowActionMenuProps> = ({
  inlineActions = [],
  menuActions = []
}): React.JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation() // Prevent row click events
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = (): void => {
    setAnchorEl(null)
  }

  // Filter out actions where `show` is explicitly false
  const visibleInlineActions = inlineActions.filter((a) => a.show !== false)
  const visibleMenuActions = menuActions.filter((a) => a.show !== false)

  return (
    <Stack
      direction="row"
      spacing={0.5}
      sx={{
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      {/* --- INLINE ACTIONS --- */}
      {visibleInlineActions.map((action, index) => (
        <Tooltip title={action.label} key={`inline-${index}`}>
          <span>
            <IconButton
              size="small"
              color={action.color || 'default'}
              disabled={action.disabled}
              onClick={(e) => {
                e.stopPropagation()
                action.onClick()
              }}
            >
              {action.icon}
            </IconButton>
          </span>
        </Tooltip>
      ))}

      {/* --- 3-DOT OVERFLOW MENU --- */}
      {visibleMenuActions.length > 0 && (
        <>
          <Tooltip title="More Actions">
            <IconButton size="small" onClick={handleMenuClick}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            onClick={(e) => e.stopPropagation()} // Prevent row click when clicking inside menu
            slotProps={{
              paper: {
                sx: {
                  minWidth: 160,
                  borderRadius: 2,
                  boxShadow: 3
                }
              }
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            {visibleMenuActions.map((action, index) => (
              <MenuItem
                key={`menu-${index}`}
                disabled={action.disabled}
                onClick={(e) => {
                  e.stopPropagation()
                  action.onClick()
                  handleMenuClose()
                }}
              >
                <ListItemIcon sx={{ color: action.color ? `${action.color}.main` : 'inherit' }}>
                  {action.icon}
                </ListItemIcon>
                <ListItemText
                  primary={action.label}
                  slotProps={{
                    primary: {
                      color: action.color === 'error' ? 'error' : 'inherit'
                    }
                  }}
                />
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Stack>
  )
}

export default RowActionMenu
